import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { formatDate, parseLocalDate, toDateStr } from '../lib/dates'

const TYPE_OPTIONS = [
  { value: 'reach_out', label: 'Reach Out', icon: '👋' },
  { value: 'call', label: 'Call', icon: '📞' },
  { value: 'schedule_job', label: 'Schedule Job', icon: '📅' },
  { value: 'other', label: 'Other', icon: '📝' },
]
const TYPE_ICON = Object.fromEntries(TYPE_OPTIONS.map(t => [t.value, t.icon]))

function todayStr() { return toDateStr(new Date()) }

// Shared follow-up reminder list, used on Client Detail, Job Detail, and
// (client-scoped) Site Visit Detail. One component so add/snooze/done/delete
// logic lives in exactly one place -- see Docs/DECISIONS_LOG.md for the
// integration decision this was built under.
export default function RemindersPanel({ clientId, jobId, title = 'Follow-Ups' }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ note: '', remind_on: todayStr(), type: 'reach_out' })
  const [busyId, setBusyId] = useState(null)

  const fetchReminders = useCallback(async () => {
    // Job-scoped views show only that job's reminders. Client-scoped-only
    // views (no jobId) show every open reminder tied to that client,
    // including ones that also carry a job_id -- the full CRM picture.
    let query = supabase.from('reminders').select('*').eq('status', 'open').order('remind_on', { ascending: true })
    query = jobId ? query.eq('job_id', jobId) : query.eq('client_id', clientId)
    const { data, error: err } = await query
    if (err) setError(err.message)
    setReminders(data || [])
    setLoading(false)
  }, [clientId, jobId])

  useEffect(() => { fetchReminders() }, [fetchReminders])

  async function addReminder(e) {
    e.preventDefault()
    if (!form.note.trim() || !form.remind_on) return
    setAdding(true); setError('')
    const { error: err } = await supabase.from('reminders').insert([{
      client_id: clientId || null,
      job_id: jobId || null,
      title: form.note.trim(),
      remind_on: form.remind_on,
      type: form.type,
      created_by: profile?.id,
    }])
    if (err) { setError('Failed to add reminder: ' + err.message); setAdding(false); return }
    setForm({ note: '', remind_on: todayStr(), type: 'reach_out' })
    setAdding(false)
    fetchReminders()
  }

  async function markDone(id) {
    setBusyId(id)
    const { error: err } = await supabase.from('reminders').update({ status: 'done', completed_at: new Date().toISOString() }).eq('id', id)
    if (err) setError('Failed to mark done: ' + err.message)
    setBusyId(null)
    fetchReminders()
  }

  async function dismiss(id) {
    setBusyId(id)
    const { error: err } = await supabase.from('reminders').update({ status: 'dismissed' }).eq('id', id)
    if (err) setError('Failed to dismiss: ' + err.message)
    setBusyId(null)
    fetchReminders()
  }

  async function snooze(id, currentRemindOn, days) {
    setBusyId(id)
    const next = parseLocalDate(currentRemindOn)
    next.setDate(next.getDate() + days)
    const { error: err } = await supabase.from('reminders').update({ remind_on: toDateStr(next) }).eq('id', id)
    if (err) setError('Failed to snooze: ' + err.message)
    setBusyId(null)
    fetchReminders()
  }

  async function remove(id) {
    if (!window.confirm('Delete this reminder?')) return
    setBusyId(id)
    const { error: err } = await supabase.from('reminders').delete().eq('id', id)
    if (err) setError('Failed to delete: ' + err.message)
    setBusyId(null)
    fetchReminders()
  }

  async function quickSiteVisit() {
    if (!clientId) return
    const { data, error: err } = await supabase.from('site_visits').insert([{ client_id: clientId, created_by: profile?.id }]).select().single()
    if (err) { setError('Failed to create site visit: ' + err.message); return }
    navigate(`/site-visits/${data.id}`)
  }

  const dueStatus = (remindOn) => {
    const diffDays = Math.floor((parseLocalDate(remindOn).getTime() - parseLocalDate(todayStr()).getTime()) / 86400000)
    if (diffDays < 0) return 'overdue'
    if (diffDays === 0) return 'today'
    return 'upcoming'
  }
  const statusColor = { overdue: '#dc2626', today: '#d97706', upcoming: '#6b7280' }

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h2 style={s.sectionTitle}>{title}</h2>
        {!loading && reminders.length > 0 && <span style={s.count}>{reminders.length} open</span>}
      </div>

      {error && <div style={s.error}>{error}</div>}

      <form onSubmit={addReminder} style={s.addRow}>
        <input
          style={s.noteInput}
          placeholder="Note — e.g. Follow up about spring renewal..."
          value={form.note}
          onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
        />
        <select style={s.typeSelect} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
          {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>
        <input
          style={s.dateInput}
          type="date"
          value={form.remind_on}
          onChange={e => setForm(f => ({ ...f, remind_on: e.target.value }))}
        />
        <button style={s.addBtn} type="submit" disabled={adding || !form.note.trim()}>
          {adding ? '...' : '+ Add'}
        </button>
      </form>

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : reminders.length === 0 ? (
        <div style={s.empty}>No open follow-ups.</div>
      ) : (
        <div style={s.list}>
          {reminders.map(r => {
            const due = dueStatus(r.remind_on)
            const isBusy = busyId === r.id
            return (
              <div key={r.id} style={s.row}>
                <div style={s.rowMain}>
                  <span style={{ ...s.dueDot, background: statusColor[due] }} />
                  <div style={s.rowInfo}>
                    <div style={s.rowNote}>{TYPE_ICON[r.type] || '📝'} {r.title}</div>
                    <div style={{ ...s.rowDate, color: statusColor[due] }}>
                      {due === 'overdue' ? 'Overdue — ' : due === 'today' ? 'Due today — ' : 'Due '}
                      {formatDate(r.remind_on)}
                    </div>
                  </div>
                </div>
                <div style={s.rowActions}>
                  {clientId && (
                    <>
                      <button style={s.quickBtn} title="New Estimate" onClick={() => navigate(`/estimates/new?client=${clientId}`)} disabled={isBusy}>📋</button>
                      <button style={s.quickBtn} title="New Site Visit" onClick={quickSiteVisit} disabled={isBusy}>🏠</button>
                      <button style={s.quickBtn} title="New Job" onClick={() => navigate(`/jobs/new?client=${clientId}`)} disabled={isBusy}>🔨</button>
                    </>
                  )}
                  <button style={s.actionBtn} title="Snooze 1 day" onClick={() => snooze(r.id, r.remind_on, 1)} disabled={isBusy}>+1d</button>
                  <button style={s.actionBtn} title="Snooze 1 week" onClick={() => snooze(r.id, r.remind_on, 7)} disabled={isBusy}>+1w</button>
                  <button style={s.doneBtn} title="Mark done" onClick={() => markDone(r.id)} disabled={isBusy}>✓</button>
                  <button style={s.dismissBtn} title="Dismiss" onClick={() => dismiss(r.id)} disabled={isBusy}>✕</button>
                  <button style={s.deleteBtn} title="Delete" onClick={() => remove(r.id)} disabled={isBusy}>🗑️</button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const s = {
  section: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  sectionTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  count: { fontSize: '12px', fontWeight: '600', color: '#6b7c6b' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  addRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  noteInput: { flex: '2 1 220px', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  typeSelect: { flex: '1 1 140px', padding: '9px 10px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', background: 'white' },
  dateInput: { flex: '1 1 140px', padding: '9px 10px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' },
  loading: { color: '#6b7c6b', padding: '12px 0', fontSize: '13px' },
  empty: { color: '#6b7c6b', fontSize: '13px', padding: '4px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', flexWrap: 'wrap' },
  rowMain: { display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: '180px' },
  dueDot: { width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px', flexShrink: 0 },
  rowInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  rowNote: { fontSize: '14px', fontWeight: '600', color: '#1a2e1a' },
  rowDate: { fontSize: '12px', fontWeight: '600' },
  rowActions: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 },
  quickBtn: { background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', padding: '5px 7px', cursor: 'pointer', fontSize: '13px' },
  actionBtn: { background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: '#374151' },
  doneBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  dismissBtn: { background: 'white', border: '1px solid #d1d5db', color: '#6b7280', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '13px' },
  deleteBtn: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '5px 7px', cursor: 'pointer', fontSize: '12px' },
}
